import styled from '@emotion/styled'
import type { CasAssignmentDtoT } from '@mathflat/domain/@entities/(Content)/Cas'
import { observer } from 'mobx-react'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { casApi } from '~/@common/api/casApi'
import { buildKaTexUserAnswer } from '~/@common/api/request/RequestScoring'
import { routeName } from '~/@common/constants'
import { useStudentAppMediaQuery } from '~/@common/hooks/useMediaQuery'
import { useRepository } from '~/@common/hooks/useRepository'
import { errorHandlerService } from '~/@common/services'
import modalService from '~/@common/services/modal.service'
import { typo } from '~/@common/styles'
import { mediaQuery } from '~/@common/styles/mediaQuery'
import { Icon } from '~/@common/ui/Icon/Icon'
import { PortalRootLayoutHeader } from '~/@common/ui/PortalRootLayoutHeader'
import GlobalHeader from '~/@pages/@widgets/(Navigation)/GlobalHeader/GlobalHeader'
import WorksheetScoringByOne from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne'
import WorksheetScoringByOneMobile from '~/@pages/@widgets/(Worksheet)/WorksheetScoring/WorksheetScoringByOne.mobile'
import { WorksheetQuickScoring } from '~/@pages/qr-worksheet/$worksheetId/$dateTime/scoring/@widgets/WorksheetQuickScoring'

import { CasBackConfirmModal, casBackConfirmModalOpt } from '../@widgets/solve/CasBackConfirmModal'
import { CasSolveView } from '../@widgets/solve/CasSolveView'
import { CasSolveViewMobile } from '../@widgets/solve/CasSolveView.mobile'
import {
  CasSubmitConfirmModal,
  casSubmitConfirmModalOpt,
} from '../@widgets/solve/CasSubmitConfirmModal'
import { useCasBackConfirmSkip } from '../@widgets/solve/useCasBackConfirmSkip'
import { useCasSubmitConfirmSkip } from '../@widgets/solve/useCasSubmitConfirmSkip'
import { CasScoringService } from './@service/CasScoring.service'

type Detail = CasAssignmentDtoT.AssignedCasDetail

const CasScoringPage = observer(() => {
  const { assignedId: assignedIdParam } = useParams<{ assignedId: string }>()
  const assignedId = Number(assignedIdParam)
  const navigate = useNavigate()
  const reviewService = useRepository(CasScoringService)

  const [assignment, setAssignment] = useState<Detail | null>(null)
  const [currentInput, setCurrentInput] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { skip, setSkip } = useCasSubmitConfirmSkip()
  const { skip: skipBack, setSkip: setSkipBack } = useCasBackConfirmSkip()
  const { isMobile } = useStudentAppMediaQuery()
  const [searchParams, setSearchParams] = useSearchParams()
  const scoringSize = searchParams.get('size')

  useEffect(() => {
    if (!Number.isFinite(assignedId)) {
      navigate(routeName.student.cas, { replace: true })
      return
    }
    casApi
      .getAssignment(assignedId)
      .then((res) => {
        setAssignment(res)
      })
      .catch((e) => {
        errorHandlerService.handle(e)
      })
  }, [assignedId, navigate])

  useEffect(() => {
    if (assignment?.accessModifierToStudent === 'PRIVATE') {
      navigate(-1)
    }
  }, [assignment?.accessModifierToStudent, navigate])

  const isCompleted = assignment?.status === 'COMPLETED'

  // COMPLETED 상태가 되면 학습지 채점 뷰용 모델로 어댑팅한다.
  useEffect(() => {
    if (!isCompleted) return
    reviewService.load(assignedId).catch((e) => errorHandlerService.handle(e))
  }, [isCompleted, assignedId, reviewService])

  // 학습지 채점 페이지와 동일하게 기본 size=all (빠른채점) 로 진입한다.
  useEffect(() => {
    if (!isCompleted) return
    if (!scoringSize) {
      setSearchParams(
        (prev) => {
          if (!prev.has('size')) prev.append('size', 'all')
          return prev
        },
        { replace: true },
      )
    }
  }, [isCompleted, scoringSize, setSearchParams])

  const problem = assignment?.problem ?? null
  const total = assignment?.maxProblemCount ?? 0
  const scored = assignment?.scoredCount ?? 0
  const isLastProblem = total > 0 && scored + 1 >= total

  const submit = async (payload: { userAnswer?: string; unknown: boolean }) => {
    if (!assignment || !problem || isSubmitting) return
    setIsSubmitting(true)
    try {
      const next = await casApi.submit(assignedId, {
        problemId: problem.id,
        ...payload,
      })
      // 마지막 문제 제출이면 결과 뷰 데이터까지 미리 로드해두고 한 번에 전환한다.
      if (next.status === 'COMPLETED') {
        await reviewService.load(assignedId)
      }
      setAssignment((prev) =>
        prev
          ? {
              ...prev,
              status: next.status,
              problem: next.problem,
              score: next.score,
              scoredCount: next.scoredCount,
              correctCount: next.correctCount,
            }
          : prev,
      )
      setCurrentInput('')
    } catch (e) {
      errorHandlerService.handle(e)
    } finally {
      setIsSubmitting(false)
    }
  }

  const requestSubmit = (payload: { userAnswer?: string; unknown: boolean }) => {
    if (skip) {
      submit(payload)
      return
    }
    modalService.openModal(
      <CasSubmitConfirmModal
        onConfirm={(skipNext) => {
          if (skipNext) setSkip(true)
          modalService.closeModal()
          submit(payload)
        }}
        onCancel={() => modalService.closeModal()}
      />,
      casSubmitConfirmModalOpt,
    )
  }

  const handleSubmit = () => {
    if (!problem) return
    const userAnswer = buildKaTexUserAnswer({
      rawAnswer: currentInput,
      answerUnits: problem.answerUnits,
    })
    requestSubmit({ userAnswer, unknown: false })
  }
  const handleUnknown = () => requestSubmit({ unknown: true })

  const handleBack = () => {
    if (isCompleted || skipBack) {
      navigate(-1)
      return
    }
    modalService.openModal(
      <CasBackConfirmModal
        onConfirm={(skipNext) => {
          if (skipNext) setSkipBack(true)
          modalService.closeModal()
          navigate(-1)
        }}
        onCancel={() => modalService.closeModal()}
      />,
      casBackConfirmModalOpt,
    )
  }

  if (!assignment) {
    return null
  }

  const sizeToggle =
    isCompleted &&
    reviewService.problemScoringColl &&
    (scoringSize === 'one' ? (
      <S.RightLink onClick={() => setSearchParams('size=all', { replace: true })}>
        <Icon name="icon_list" size={20} color={'var(--Colors-Gray-900)'} />
        <span>빠른채점</span>
      </S.RightLink>
    ) : (
      <S.RightLink onClick={() => setSearchParams('size=one', { replace: true })}>
        <Icon name="icon_description_paper" size={20} color={'var(--Colors-Gray-900)'} />
        <span>한문제씩</span>
      </S.RightLink>
    ))

  return (
    <>
      <PortalRootLayoutHeader>
        <GlobalHeader
          SlotCenter={assignment.title}
          SlotRight={sizeToggle || undefined}
          prevLinkProps={{
            to: routeName.student.cas,
            onClick: (e) => {
              e.preventDefault()
              handleBack()
            },
          }}
        />
      </PortalRootLayoutHeader>
      {isCompleted ? (
        reviewService.worksheet &&
        reviewService.problemScoringColl &&
        reviewService.problemScoringViewOption ? (
          scoringSize === 'one' ? (
            isMobile ? (
              <WorksheetScoringByOneMobile
                problemScoringColl={reviewService.problemScoringColl}
                viewOption={reviewService.problemScoringViewOption}
                onSubmit={() => undefined}
                studentWorksheet={reviewService.studentWorksheet}
                type={reviewService.worksheet.type}
              />
            ) : (
              <WorksheetScoringByOne
                isSubmittable={false}
                problemScoringColl={reviewService.problemScoringColl}
                viewOption={reviewService.problemScoringViewOption}
                onSubmit={() => undefined}
                studentWorksheet={reviewService.studentWorksheet}
                worksheet={reviewService.worksheet}
              />
            )
          ) : (
            <WorksheetQuickScoring
              score={reviewService.studentWorksheet?.score ?? 0}
              problemScoringColl={reviewService.problemScoringColl}
              problemScoringViewOption={reviewService.problemScoringViewOption}
              gradingCount={reviewService.problemScoringColl.gradingCount}
              clearAllAnswer={() => undefined}
              openConfirmToSubmitAnswersModal={() => undefined}
              isSubmittable={false}
              isDeletable={false}
            />
          )
        ) : null
      ) : isMobile ? (
        <CasSolveViewMobile
          assignment={assignment}
          problem={problem}
          currentInput={currentInput}
          isSubmitting={isSubmitting}
          isLastProblem={isLastProblem}
          onChangeInput={setCurrentInput}
          onSubmit={handleSubmit}
          onUnknown={handleUnknown}
        />
      ) : (
        <CasSolveView
          assignedId={assignedId}
          assignment={assignment}
          problem={problem}
          currentInput={currentInput}
          isSubmitting={isSubmitting}
          isLastProblem={isLastProblem}
          onChangeInput={setCurrentInput}
          onSubmit={handleSubmit}
          onUnknown={handleUnknown}
        />
      )}
    </>
  )
})

export default CasScoringPage

const S = {
  RightLink: styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 2px;
    ${typo.caption01};
    color: var(--Colors-Gray-900);
    font-weight: bold;
    cursor: pointer;

    ${mediaQuery.underDesktop} {
      ${typo.body02};
      font-weight: normal;
      color: var(--Colors-Gray-600);
      svg {
        color: var(--Colors-Gray-600);
      }
    }
  `,
}
